import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntities as getStations } from 'app/entities/station/station.reducer';
import { getEntities as getTypes } from 'app/entities/session-type/session-type.reducer';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { SessionStatus } from 'app/shared/model/enumerations/session-status.model';
import { createEntity, getEntity, updateEntity } from './session.reducer';

export const SessionUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const stations = useAppSelector(state => state.station.entities);
  const users = useAppSelector(state => state.userManagement.users);
  const types = useAppSelector(state => state.type.entities);
  const sessionEntity = useAppSelector(state => state.session.entity);
  const loading = useAppSelector(state => state.session.loading);
  const updating = useAppSelector(state => state.session.updating);
  const updateSuccess = useAppSelector(state => state.session.updateSuccess);
  const sessionStatusValues = Object.keys(SessionStatus);

  const handleClose = () => {
    navigate('/session');
  };

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id));
    }

    dispatch(getStations({}));
    dispatch(getUsers({}));
    dispatch(getTypes({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  // eslint-disable-next-line complexity
  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.startDateTime = convertDateTimeToServer(values.startDateTime);
    values.endDateTime = convertDateTimeToServer(values.endDateTime);

    const entity = {
      ...sessionEntity,
      ...values,
      station: stations.find(it => it.id.toString() === values.station?.toString()),
      operator: users.find(it => it.id.toString() === values.operator?.toString()),
      type: types.find(it => it.id.toString() === values.type?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          startDateTime: displayDefaultDateTime(),
          endDateTime: displayDefaultDateTime(),
        }
      : {
          status: 'PLANNED',
          ...sessionEntity,
          startDateTime: convertDateTimeFromServer(sessionEntity.startDateTime),
          endDateTime: convertDateTimeFromServer(sessionEntity.endDateTime),
          station: sessionEntity?.station?.id,
          operator: sessionEntity?.operator?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="cabmanApp.session.home.createOrEditLabel" data-cy="SessionCreateUpdateHeading">
            <Translate contentKey="cabmanApp.session.home.createOrEditLabel">Create or edit a Session</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="session-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('cabmanApp.session.status')}
                id="session-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {sessionStatusValues.map(sessionStatus => (
                  <option value={sessionStatus} key={sessionStatus}>
                    {translate('cabmanApp.SessionStatus.' + sessionStatus)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('cabmanApp.session.startDateTime')}
                id="session-startDateTime"
                name="startDateTime"
                data-cy="startDateTime"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('cabmanApp.session.endDateTime')}
                id="session-endDateTime"
                name="endDateTime"
                data-cy="endDateTime"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="session-station"
                name="station"
                data-cy="station"
                label={translate('cabmanApp.session.station')}
                type="select"
              >
                <option value="" key="0" />
                {stations
                  ? stations.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="session-operator"
                name="operator"
                data-cy="operator"
                label={translate('cabmanApp.session.operator')}
                type="select"
              >
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField id="session-type" name="type" data-cy="type" label={translate('cabmanApp.session.type')} type="select">
                <option value="" key="0" />
                {types
                  ? types.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.name}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/session" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default SessionUpdate;
